import { Trans, useLingui } from "@lingui/react/macro";
import {
  patientKeys,
  updateEconomicUnit,
  useEconomicUnits,
} from "@/api/Patients";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { FilledButton } from "@medoma/components/Button/Button";
import Form from "@/components/Form/Form";
import { Loading } from "@medoma/components/Loading/Loading";
import { deducedError } from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import type { IEconomicUnit } from "@medoma/models/patients";
import Select from "@medoma/components/Select/Select";
import styles from "./EditArea.module.scss";

export const EditEconomicUnit = ({
  currentEconomicUnit,
  onSuccess,
  patientId,
}: {
  currentEconomicUnit: IEconomicUnit | undefined | null;
  onSuccess: () => void;
  patientId: string;
}) => {
  const { t } = useLingui();
  const currentEconomicUnitId = currentEconomicUnit
    ? currentEconomicUnit.id
    : null;
  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      economicUnitId: currentEconomicUnitId,
    },
  });
  const queryClient = useQueryClient();
  const {
    data: economicUnits,
    isPending: isPendingEconomicUnits,
    isError: isErrorEconomicUnits,
  } = useEconomicUnits();
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (economicUnit: number) =>
      updateEconomicUnit({ patientId, economicUnit }),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: patientKeys.detail(patientId),
      });
      onSuccess();
    },
  });

  return (
    <Form
      onSubmit={handleSubmit((formData) => {
        if (formData.economicUnitId) {
          mutate(formData.economicUnitId);
        }
      })}
    >
      {isPending || isSuccess ? (
        <Loading message={t`Ändrar ekonomisk enhet`} />
      ) : (
        <>
          {errors.root?.server?.message && !isDirty ? (
            <ErrorMessage message={errors.root.server.message} />
          ) : undefined}
          <Form.Row>
            <div className={styles.select}>
              {isPendingEconomicUnits ? (
                <Loading message={t`Hämtar ekonomiska enheter`} />
              ) : isErrorEconomicUnits ? (
                <ErrorMessage
                  message={t`Kunde inte hämta ekonomiska enheter`}
                />
              ) : (
                <Select
                  label={t`Ekonomisk enhet`}
                  hideLabel
                  {...register("economicUnitId")}
                  errorMessage={errors.economicUnitId?.message}
                >
                  {economicUnits.map((economicUnit) => (
                    <option value={economicUnit.id} key={economicUnit.id}>
                      {economicUnit.displayName}
                    </option>
                  ))}
                </Select>
              )}
            </div>
          </Form.Row>
          {isDirty ? (
            <FilledButton type="submit">
              <Trans>Spara ändringar</Trans>
            </FilledButton>
          ) : (
            <></>
          )}
        </>
      )}
    </Form>
  );
};
